import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { resolveRequest } from '@redirector/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.js';
import { cn } from '@/lib/utils.js';
import { apiSend } from '../lib/api.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';
import { SimulationChart, type SimulationRow } from './SimulationChart.js';

type ResolveResult = {
  input: { hostname: string; path: string; pathNorm: string; query: string };
  match: null | {
    groupId: number;
    groupName: string;
    domainId: number;
    hostname: string;
    entryId: number;
    entryPath: string;
    entryLabel: string;
  };
  outcome: 'redirect' | 'fallback' | 'not_found';
  reason: string | null;
  location: string | null;
  destinations: SimulationRow[];
  snapshotVersion: number;
};

const REASONS: Record<string, string> = {
  unknown_host: 'No group is configured for that hostname.',
  unknown_path: 'That hostname is configured, but there is no path configured for this URL.',
  entry_disabled: 'That path exists but is currently disabled.',
  no_enabled_destinations: 'That path exists but every destination under it is paused.',
};

const SAMPLES = 1_000;

// The scheme is left off deliberately: `src/styles/tokens.test.ts` walks every
// source file and rejects an external origin, which is the right guard for a
// console that must load nothing from the internet. The resolver accepts a bare
// hostname and path anyway (Plan 2 Task 7).
const PLACEHOLDER = 'timpromos.com.br/BTW/landing-slug?utm_source=email';

type FactProps = { term: string; mono?: boolean; children: ReactNode };

/** One term-and-value pair in the match panel. */
function Fact({ term, mono, children }: FactProps) {
  return (
    <div>
      <dt className="text-[length:var(--text-micro)] uppercase tracking-wide text-ink-subtle">
        {term}
      </dt>
      <dd
        className={cn(
          'mt-(--space-1) text-[length:var(--text-small)]',
          mono === true && 'font-mono break-all',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

export function ResolveScreen() {
  /* `?url=` is how the URL page's Test action arrives here: the operator asked
     to test one specific URL, so it is filled in and run without them having to
     paste it back in. It stays a plain query parameter, which also makes a
     test a shareable link. */
  const [search] = useSearchParams();
  const requested = search.get('url') ?? '';

  const [url, setUrl] = useState(requested);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResolveResult | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (candidate: string) => {
    setError(null);

    const parsed = resolveRequest.safeParse({ url: candidate, samples: SAMPLES });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a URL to test');
      return;
    }

    setBusy(true);
    try {
      setResult(await apiSend<ResolveResult>('POST', '/api/resolve', parsed.data));
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : 'Could not test that URL');
    } finally {
      setBusy(false);
    }
  }, []);

  /* Runs once per URL handed in, not on every render: `ran` remembers what has
     already been tested, so editing the box afterwards does not fight with a
     re-run of the parameter still sitting in the address bar. */
  const ran = useRef<string | null>(null);
  useEffect(() => {
    if (requested === '' || ran.current === requested) return;
    ran.current = requested;
    setUrl(requested);
    void run(requested);
  }, [requested, run]);

  function submit(event: FormEvent) {
    event.preventDefault();
    void run(url);
  }

  return (
    <section aria-labelledby="resolve-heading" className="flex flex-col gap-(--space-5)">
      <header>
        <p className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle">
          Diagnostics
        </p>
        <h1 id="resolve-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
          Test a URL
        </h1>
        <p className="mt-(--space-2) max-w-[64ch] text-ink-muted">
          Paste any entry URL to see exactly where a visitor would be sent right now, using the same
          routing table that is serving live traffic.
        </p>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-(--space-3) sm:flex-row sm:items-end" noValidate>
        <div className="flex-1">
          <Field label="URL to test" error={error ?? undefined}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                autoFocus
                spellCheck={false}
                placeholder={PLACEHOLDER}
                className="w-full rounded-(--radius-md) border border-rule-strong bg-surface-raised px-(--space-3) py-(--space-2) font-mono text-[length:var(--text-small)] text-ink"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={url}
                onChange={(event) => setUrl(event.target.value)}
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Testing…' : 'Test URL'}
        </Button>
      </form>

      {result !== null && (
        <div className="flex flex-col gap-(--space-4)">
          {result.outcome === 'redirect' ? (
            <Callout tone="ok">
              Redirects to{' '}
              <span className="font-mono break-all">{result.location}</span>
            </Callout>
          ) : (
            <Callout tone="warn">
              <strong>
                {result.reason !== null && REASONS[result.reason] !== undefined
                  ? REASONS[result.reason]
                  : 'This URL does not match a configured path.'}
              </strong>
              {result.location !== null ? (
                <>
                  {' '}
                  Traffic goes to the fallback:{' '}
                  <span className="font-mono break-all">{result.location}</span>
                </>
              ) : (
                ' No fallback is configured, so the visitor receives a 404.'
              )}
            </Callout>
          )}

          {/* The six facts behind the verdict, bounded as one panel so they
              read as the evidence for the callout above rather than as loose
              text trailing off the page. */}
          <Card className="gap-0">
            <CardHeader>
              <CardTitle className="text-[length:var(--text-small)] uppercase tracking-wide text-ink-subtle">
                How this URL was matched
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-(--space-4)">
              <dl className="grid gap-(--space-4) sm:grid-cols-2">
                <Fact term="Matched hostname" mono>
                  {result.input.hostname}
                </Fact>
                <Fact term="Match key" mono>
                  {result.input.pathNorm}
                </Fact>
                <Fact term="Configured path" mono>
                  {result.match?.entryPath ?? '—'}
                </Fact>
                <Fact term="Group">{result.match?.groupName ?? '—'}</Fact>
                <Fact term="Forwarded query" mono>
                  {result.input.query === '' ? 'none' : result.input.query}
                </Fact>
                <Fact term="Routing table version">{result.snapshotVersion}</Fact>
              </dl>
            </CardContent>
          </Card>

          {result.destinations.length > 0 && (
            <div className="flex flex-col gap-(--space-2)">
              <h2 className="text-[length:var(--text-lead)]">
                Simulated over {SAMPLES.toLocaleString('en-GB')} requests
              </h2>
              <SimulationChart rows={result.destinations} samples={SAMPLES} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
